// Slice-internal Konva renderer for the textbox slice (PRD-RTE §TR1–TR7).
//
// The view-mode renderer rasterizes the formatted HTML text via an inline
// SVG `<foreignObject>` (built by `textbox-svg-renderer.ts`) into an
// `HTMLImageElement` and draws it onto a `Konva.Image`. This delegates all
// text layout to the browser's own engine — guaranteeing parity with the
// contentEditable overlay used in edit mode (B5) and faithfully preserving
// all CSS properties (B6, e.g. font-weight: 100).
//
// Rasterization is gated by a 5%-scale-delta threshold (PRD-RTE §TR4) plus a
// render-hash check (text + style fields), so style-only changes invalidate
// the bitmap immediately while sub-threshold zoom changes reuse it.
//
// The asynchronous Image.onload race is controlled via an RxJS
// `Subject<RasterRequest>` piped through `switchMap` (PRD-RTE §TR2) — a
// newer raster request automatically discards an in-flight older load.

import Konva from 'konva';
import { Observable, Subject, type Subscription } from 'rxjs';
import { debounceTime, switchMap } from 'rxjs/operators';
import type { SliceRenderContext } from '../../core/object-registry.js';
import type { ObjectId } from '../../core/bus/branded-types.js';
import { getDpr } from '../../core/dpr.helper.js';
import { defaultFontIndexForScale, fontIndexToWorldUnits } from '../../core/viewport/zoom-cluster.service.js';
import { wireHoverFillGroup } from '../../core/konva/hover-cursor.js';
import { HANDLE_HOVER_FILL } from '../../constants/whiteboard-config.constants.js';
import { konvaDashPattern, konvaDropShadowAttrs } from '../../objects/konva-decoration.helper.js';
import { TEXTBOX_LINE_HEIGHT } from '../../objects/board-object-renderer.js';
import type { TextBox } from './textbox.interface.js';
import { buildTextboxSvg } from './textbox-svg-renderer.js';
import { embeddedFontFaceCss } from './board-font-embed.helper.js';
import { BOARD_FONT_FAMILY_NAME } from './textbox-html-builder.js';
import { isInsideBoxHeight, isLauncherVisible, openLinkUrl } from './link-open.js';
import type { LinkTarget, ChecklistLinePosition, TextLineRect } from './textbox-svg-renderer.js';
import { countVisibleTaskItems } from './text-document-migration.js';
import {
  EMPTY_BITMAP,
  LAST_COMPUTED_SCALE_ATTR,
  LAST_RENDER_HASH_ATTR,
  OVERFLOW_INDICATOR,
  RE_RASTERIZE_THRESHOLD,
  type RasterRequest,
} from './textbox.types.js';

const TEXTBOX_BG_NAME = 'textbox-bg';
const TEXTBOX_CONTENT_NAME = 'textbox-content';

/** Konva group name for the bottom-right overset "+" indicator (TR3). */
const OVERFLOW_INDICATOR_NAME = 'textbox-overflow-indicator';
/** Named so `layoutOverflowIndicator` can re-size them on every zoom tick. */
const OVERFLOW_BOX_NAME = 'textbox-overflow-box';
const OVERFLOW_GLYPH_NAME = 'textbox-overflow-glyph';
/** Holder for the invisible click targets that sit over the link launchers. */
const LINK_HOTSPOTS_NAME = 'textbox-link-hotspots';
/** Holder for the invisible click targets that sit over each checklist row. */
const CHECKLIST_HOTSPOTS_NAME = 'textbox-checklist-hotspots';

/**
 * Geometric-fallback-only approximation of the checkbox width factor. MUST
 * equal `CHECKLIST_BOX_SIZE_FACTOR` in `textbox-html-builder.ts` (duplicated
 * as a literal, not imported, to keep this module decoupled from the HTML
 * builder) — drift-guarded by a unit test that imports both constants and
 * asserts equality, the same pattern used for every other duplicated
 * "twin-site" checklist constant in this codebase.
 * Only used for the brief pre-first-raster window in `rebuildChecklistHotspots`
 * — the steady state always uses the REAL measured checkbox width instead.
 */
export const CHECKLIST_BOX_SIZE_FACTOR_FALLBACK = 1;

/**
 * Trailing-raster debounce window (ms) for the zoom-settle raster (O2D.W-PRD-029
 * §TR2). The trailing raster fires only AFTER a wheel burst genuinely settles
 * (~150ms of quiet), never mid-burst — a value in the ~120-200ms range.
 */
const TRAILING_RASTER_DEBOUNCE_MS = 150;

/**
 * Base VERTICAL (top/bottom) inner padding of a textbox, in world units, at
 * cluster factor 1 (viewport scale < 150%). Reduced from an earlier 14 (review
 * 01, MAJOR finding, Linse A): at 14/28 the smallest drag-created textbox
 * (`MIN_DRAG_WIDTH`/`MIN_DRAG_HEIGHT` = 40×28 in `textbox-defaults.factory.ts`)
 * collapsed to ~0 visible content area. 4/8 leaves comfortable room at the
 * minimum drag size while staying visible for testing.
 */
export const TEXTBOX_PADDING = 4;

/**
 * Left/right padding is EXPERIMENTALLY wider than top/bottom (2026-08-16,
 * Anke — "links und rechts ca. doppelt so groß") — a plain ratio on top of
 * the vertical base, not an independently tuned value. Test value; not final.
 */
export const TEXTBOX_PADDING_HORIZONTAL_RATIO = 2;

/**
 * Font index a new textbox got at scale 1 when `TEXTBOX_PADDING` was
 * calibrated (2026-08-16): the log-linear midpoint 12.5, which rounds to 13
 * ("5.1"). Pinned here since Anke moved the 100 % start size to "4.1"
 * (2026-10-02): the padding stays the same SHARE of the font ("proportional
 * zur Schrift"), so at 100 % it shrinks with the font instead of staying 4 WU.
 */
const TEXTBOX_PADDING_CALIBRATION_FONT_INDEX = 12.5;

/** `TEXTBOX_PADDING`'s ratio to the font size it was calibrated against. */
const TEXTBOX_PADDING_TO_FONT_RATIO =
  TEXTBOX_PADDING / fontIndexToWorldUnits(TEXTBOX_PADDING_CALIBRATION_FONT_INDEX);

/**
 * EXPERIMENTAL (2026-08-16, Anke) — VERTICAL (top/bottom) padding a NEW
 * textbox gets, in world units. CREATION-TIME ONLY: called once by
 * `createTextBoxDefaults` (review 01, MAJOR findings — Anke, 2026-08-16:
 * "der paddingwert soll sich nicht ändern, wenn man ein vorhandenes
 * textfeld zoomt ... nur im moment der erstellung"). The result is frozen
 * onto `TextBox.paddingV`; nothing re-derives it from a live viewport scale
 * afterwards — same freeze-at-creation contract as `creationDefaults`'s
 * `textWidth`/`textHeight`/`textFontSize`.
 *
 * Bugreport 2026-08-18 (Anke) — "bei kleinen Zoomstufen (10%) ein winziges
 * oder gar kein Padding". Originally banded by the coarse 1/2/4/8 cluster
 * factor (`getClusterFactor`), same as `creationDefaults`'s `textWidth`/
 * `textHeight` at the time. That factor is constant 1 for every zoom < 150%,
 * so padding stayed flat at 4 WU across the whole 100%→10% range — fine
 * back when `textFontSize` was ALSO flat there (the since-fixed
 * `safeDefaultFontSize` cap, see `zoom-cluster.service.ts`), but once that
 * cap was removed the font grew continuously up to ~487 WU at 10% zoom
 * while padding stayed pinned at 4 WU: down from ~5.6% of the font size at
 * 100% zoom to under 1% at 10% zoom — imperceptible next to the now much
 * larger text. Padding is scaled proportionally to `textFontSize` instead,
 * preserving the exact 100%-zoom reference value (4 WU) while growing in
 * step with the font at low zoom, same as `textHeight`/`textWidth` already
 * do.
 */
export function textboxPaddingForScale(scale: number): number {
  return TEXTBOX_PADDING_TO_FONT_RATIO * fontIndexToWorldUnits(defaultFontIndexForScale(scale));
}

/** Same banding as `textboxPaddingForScale`, but for the wider left/right inset. */
export function textboxPaddingHorizontalForScale(scale: number): number {
  return textboxPaddingForScale(scale) * TEXTBOX_PADDING_HORIZONTAL_RATIO;
}

/** Build the Konva attrs that describe the optional drop shadow. */
function bgShadowAttrs(obj: Readonly<TextBox>): Konva.RectConfig {
  return konvaDropShadowAttrs(obj.boxShadow === 'on');
}

/** Translate `borderStyle` into a Konva dash pattern; `solid` uses no dashes. */
function borderDashPattern(obj: Readonly<TextBox>): number[] {
  return konvaDashPattern(obj.borderStyle, obj.borderWidth);
}

/**
 * Render-hash over all visible attributes (PRD-RTE §TR4). A change to any of
 * these fields forces immediate re-rasterization regardless of the scale gate.
 *
 * `obj.width` / `obj.height` are included so that resizing the textbox rebuilds
 * the bitmap at the new dimensions: the SVG-foreignObject re-lays-out the text
 * at the new width (reflow at constant font size) instead of Konva stretching
 * the old bitmap, which distorted the glyphs. The `switchMap` raster pipeline
 * keeps only the latest in-flight load, so a rapid resize paints just the final
 * geometry. Pure position mutations (x / y / rotation) stay out — they update
 * via Konva attrs without rebuilding the bitmap.
 */
function computeRenderHash(obj: Readonly<TextBox>): string {
  // Document content (TipTap JSON) is the canonical source of textual content
  // after PRD-RTE-TIPTAP. Stringify covers text runs, marks (bold, italic,
  // color, fontSize) and list-node shape — superseding the previous
  // `obj.text` + `obj.listType` pair in a single hash component.
  const docHash = obj.document ? JSON.stringify(obj.document) : '';
  return [
    docHash,
    obj.color,
    obj.fontSize,
    obj.fontWeight,
    obj.background ?? '',
    obj.borderColor ?? '',
    obj.borderWidth,
    obj.borderStyle ?? 'solid',
    obj.cornerRadius ?? 0,
    obj.width,
    obj.height,
    // O2D.W-PRD-032 §TR1 (DEFECT B): alignment is a visible attribute — an
    // align-only change on a selected (not-editing) textbox must bump the hash
    // so `updateNode` re-rasterizes the bitmap at the new h/v alignment. One
    // change covers both axes (§TR3); no per-axis branch.
    obj.hAlign,
    obj.vAlign,
  ].join('|');
}

/** URI-encode the SVG XML into a `data:image/svg+xml` URL. */
function svgToDataUrl(svg: string): string {
  return 'data:image/svg+xml;charset=utf-8,' + encodeURIComponent(svg);
}

/**
 * Emit shape of `rasterize$` (PRD-RTE-OVERFLOW §TR2). Bundles the loaded raster
 * image with the overflow flags measured by `buildTextboxSvg`, so the raster
 * subscriber can drive both the content bitmap AND the overflow indicator from
 * a single emission.
 */
interface RasterEmit {
  readonly img: HTMLImageElement;
  readonly hasOverflow: boolean;
  readonly requiredHeight: number;
  /** Measured clickable link launchers (world units, box-relative). */
  readonly linkTargets: ReadonlyArray<LinkTarget>;
  /** Measured checklist item positions (world units, box-relative). */
  readonly checklistLines: ReadonlyArray<ChecklistLinePosition>;
  /** Measured text line areas (world units, box-relative). */
  readonly textLines: ReadonlyArray<TextLineRect>;
  /**
   * The viewport scale this raster was built at (carried from the originating
   * `RasterRequest`). The paint subscriber stamps `LAST_COMPUTED_SCALE_ATTR`
   * from this value AFTER the bitmap is drawn, so the attr always reflects the
   * scale that was actually PAINTED — never a scale that was merely enqueued and
   * then switchMap-cancelled before decoding (O2D.W-PRD-029 §TR1).
   */
  readonly scale: number;
}

/**
 * Cold observable that builds the SVG, loads it into an HTMLImageElement, and
 * emits the loaded image plus its overflow flags on `onload`. Teardown detaches
 * the handlers so a late-arriving onload from a discarded request cannot leak
 * through after `switchMap` has unsubscribed.
 *
 * Bugreport 2026-08-31 (Stefan): a freshly drawn-and-committed textbox
 * rendered in the SYSTEM fallback font in view mode, not D-DIN — matching the
 * live contentEditable overlay only after a later edit re-triggered a raster.
 * Root cause: the `<img>` this function decodes is an independent rendering
 * context, NOT the page's normal DOM/Canvas text — it paints with whatever
 * font the browser's engine already has fully loaded and cached AT THE
 * MOMENT `img.src` is set, and (unlike ordinary page text) never repaints
 * itself once a still-loading webfont finishes. On a fresh page load, D-DIN
 * is typically still mid-download the first time a textbox is drawn: the
 * live edit overlay (`.textbox-editor`, `font-family: "D-DIN", …` —
 * `whiteboard-canvas.css`) is what actually TRIGGERS the browser's D-DIN
 * fetch (nothing on an empty board referenced it before), but typing and
 * clicking out can easily finish before that fetch does. The fix waits for
 * `document.fonts.ready` (already loading by the time this fires, thanks to
 * the edit overlay) before building/decoding the raster — same guarded
 * pattern `o2d-whiteboard.element.ts`'s `#redrawOnFontsReady` already uses
 * for Konva canvas text; `document.fonts` is absent under happy-dom, so the
 * guard also keeps the unit tests untouched.
 *
 * That timing fix turned out to be necessary but NOT sufficient (follow-up
 * bugreport, Stefan on Linux/Chrome): even once D-DIN had genuinely finished
 * loading on the page, the raster still fell back to Ubuntu (the OS's
 * default sans-serif). The `<img>` decoding this SVG is a browser-spec
 * "restricted image context" that does not reliably share the parent
 * document's `@font-face` cache with every browser/OS — see
 * `board-font-embed.helper.ts`'s module doc. The fix there embeds the font's
 * ACTUAL bytes as a `data:` URI inside the SVG itself, so the isolated image
 * context never has to resolve an external font at all.
 */
function rasterize$(req: RasterRequest): Observable<RasterEmit> {
  return new Observable<RasterEmit>((subscriber) => {
    let cancelled = false;
    let img: HTMLImageElement | null = null;

    const build = (fontFaceCss: string): void => {
      if (cancelled) return;
      // PRD-RTE-OVERFLOW §TR2 — `buildTextboxSvg` returns an `SvgRasterResult`
      // (`svg` + overflow flags). The flags are measured on a zoom-independent
      // world-unit element and travel with the image so the subscriber can toggle
      // the overflow indicator and refresh the cached auto-expand height (TR3/TR4).
      const result = buildTextboxSvg(req.obj, req.scale, req.dpr, fontFaceCss);
      img = new Image();
      img.onload = () => {
        subscriber.next({
          img: img as HTMLImageElement,
          hasOverflow: result.hasOverflow,
          requiredHeight: result.requiredHeight,
          linkTargets: result.linkTargets,
          checklistLines: result.checklistLines,
          textLines: result.textLines,
          scale: req.scale,
        });
        subscriber.complete();
      };
      img.onerror = () => subscriber.complete();
      img.src = svgToDataUrl(result.svg);
    };

    // `document.fonts` is absent under happy-dom (no real browser font
    // engine) — take the exact old SYNCHRONOUS fast path there (`build('')`,
    // same tick) rather than adding a needless microtask hop through
    // `embeddedFontFaceCss` that only ever resolves empty in that
    // environment anyway (`document.styleSheets` is empty there too). This
    // keeps the whole existing test suite's synchronous-MockImageCtor
    // assumption intact; the font-embedding path itself is covered by its
    // own dedicated tests below, which stub `document.fonts` explicitly.
    const fonts: FontFaceSet | undefined = document.fonts;
    if (fonts?.ready) {
      // Review-Lauf 02 (MINOR, Linse A): defensive `.catch` — every current
      // sub-path (`embeddedFontFaceCss`, its `fetch`/`FileReader` calls) is
      // already try/catch-guarded and never rejects in practice, but without
      // this, a future regression that DID introduce an unguarded throw
      // would leave this `subscriber` hanging forever (no `next`, no
      // `complete`, no `error`) instead of falling back cleanly like the
      // existing `img.onerror` path does.
      void Promise.all([fonts.ready, embeddedFontFaceCss(BOARD_FONT_FAMILY_NAME)]).then(
        ([, fontFaceCss]) => build(fontFaceCss),
      ).catch(() => build(''));
    } else {
      build('');
    }

    return () => {
      cancelled = true;
      if (img) {
        img.onload = null;
        img.onerror = null;
      }
    };
  });
}

/**
 * Build the bottom-right overset "+" indicator group (PRD-RTE-OVERFLOW §TR3).
 *
 * A small square (`Konva.Rect`) with a centered "+" glyph (`Konva.Text`),
 * positioned at `(obj.width - SIZE - PAD, obj.height - SIZE - PAD)` in world
 * units. Starts hidden; the raster subscriber toggles `visible()` from the
 * measured `hasOverflow` flag (never while editing).
 */
function createOverflowIndicator(obj: Readonly<TextBox>, scale: number): Konva.Group {
  const { STROKE, FILL, GLYPH } = OVERFLOW_INDICATOR;
  const indicator = new Konva.Group({
    name: OVERFLOW_INDICATOR_NAME,
    visible: false,
  });
  indicator.add(
    new Konva.Rect({
      name: OVERFLOW_BOX_NAME,
      x: 0,
      y: 0,
      fill: FILL,
      stroke: STROKE,
      strokeWidth: 1,
      // Keep the outline hairline-thin at every zoom, like the handles.
      strokeScaleEnabled: false,
    }),
  );
  indicator.add(
    new Konva.Text({
      name: OVERFLOW_GLYPH_NAME,
      x: 0,
      text: GLYPH,
      fontStyle: OVERFLOW_INDICATOR.GLYPH_WEIGHT,
      align: 'center',
      verticalAlign: 'middle',
      fill: STROKE,
      listening: false,
    }),
  );
  layoutOverflowIndicator(indicator, obj.width, obj.height, scale);
  return indicator;
}

/**
 * Size and position the overset marker, in CSS px converted to world units.
 *
 * Anke, 2026-07-18 — the marker must (a) stay the same size on screen at every
 * zoom level and (b) match the corner handles, so it is measured in screen px
 * and divided by `scale` here rather than being stored in world units. It must
 * ALSO clear the corner resize handle, which is centred on the corner and so
 * reaches inwards; `PAD_PX` carries that clearance.
 *
 * Called from three places, all of which must stay in sync or the marker drifts:
 * node creation, every viewport tick (NOT gated by the re-raster threshold —
 * that gate exists to avoid needless rasterization, but a marker that skips zoom
 * ticks visibly lags), and `updateNode` when the box is resized.
 */
function layoutOverflowIndicator(
  indicator: Konva.Group,
  width: number,
  height: number,
  scale: number,
): void {
  const s = scale || 1;
  const size = OVERFLOW_INDICATOR.SIZE_PX / s;
  const pad = OVERFLOW_INDICATOR.PAD_PX / s;
  // The group carries `offset = size / 2`, so Konva scales it about its own
  // CENTRE on hover (the square handles do the same) — which means `position`
  // is that centre, not the top-left corner. The box therefore still spans
  // [width - pad - size, width - pad] as before.
  indicator.offset({ x: size / 2, y: size / 2 });
  indicator.position({ x: width - pad - size / 2, y: height - pad - size / 2 });
  indicator.findOne<Konva.Rect>(`.${OVERFLOW_BOX_NAME}`)?.size({ width: size, height: size });
  indicator.findOne<Konva.Text>(`.${OVERFLOW_GLYPH_NAME}`)?.setAttrs({
    width: size,
    height: size,
    fontSize: size,
    // Optical centring — see GLYPH_NUDGE_PX. In screen px, so the nudge does not
    // grow into a visible offset as you zoom in.
    y: OVERFLOW_INDICATOR.GLYPH_NUDGE_PX / s,
  });
}

/**
 * Anke, 2026-07-19 — rebuild the invisible click targets that sit over the link
 * launchers painted into the raster.
 *
 * These exist as REAL Konva nodes rather than a hit test on the parent group,
 * and that is the whole point. The textbox group is draggable, and Konva begins
 * a drag on `mousedown`. A handler on the group itself cannot call the drag off
 * — the group IS the draggable node, so cancelling the bubble there is too late
 * and the box ends up stuck to the cursor (Ankes Fehlerbericht: nach dem
 * Zurueckwechseln aus dem Link-Tab hing die Textbox an der Maus). A CHILD node
 * can stop the press before it ever reaches the group — exactly how the overset
 * indicator solves the same problem.
 *
 * Targets clipped away below the box edge are skipped: the measuring pass runs
 * with the height released, so it also reports launchers that are not painted.
 */
function rebuildLinkHotspots(
  holder: Konva.Group,
  targets: ReadonlyArray<LinkTarget>,
  boxHeight: number,
): void {
  holder.destroyChildren();
  for (const [index, t] of targets.entries()) {
    if (!isLauncherVisible(t, boxHeight)) continue;
    const hotspot = new Konva.Rect({
      x: t.x,
      y: t.y,
      width: t.width,
      height: t.height,
      // Needs a fill to be hit-testable, but must never be seen. Konva ignores
      // opacity when building the hit graph, so `opacity: 0` stays clickable.
      fill: 'black',
      opacity: 0,
      listening: true,
    });
    hotspot.setAttr('o2dHref', t.href);
    // The launcher's index in the MEASUREMENT (stable even when an earlier one
    // is clipped and gets no hotspot) — read by `__linkHotspotClientRect`.
    hotspot.setAttr('o2dLinkIndex', index);
    holder.add(hotspot);
  }
}

/**
 * Bugreport 2026-08-16 (user) — rebuild the invisible click targets that sit
 * over each checklist item's CHECKBOX.
 *
 * The checklist toggle used to be a hit test run inside a `group.on('click
 * tap', ...)` handler — exactly the "handler on the group itself" mistake
 * `rebuildLinkHotspots` above already warns about: the group IS the draggable
 * node, so cancelling the bubble on `click` (which only fires on MOUSE UP) is
 * too late — a real mouse click almost always has a few px of jitter between
 * press and release, enough to arm Konva's own drag on the PRECEDING
 * `mousedown`, and by the time `click` ran the drag was already live,
 * leaving the textbox stuck to the cursor. Moving the SAME hit test onto a
 * `mousedown`-level handler bound to the group itself does NOT fix this
 * either — cancelling the bubble stops propagation to ANCESTORS, not to
 * Konva's own internal drag-arming logic bound to that same node (confirmed
 * empirically: a `mousedown`-level guard on `group` still let jittery clicks
 * arm a drag and swallow the following `click`). One real per-item CHILD
 * hotspot node, exactly like `rebuildLinkHotspots`, is what actually works:
 * its `mousedown` cancels the bubble before it ever REACHES the draggable
 * group.
 *
 * Bugreport 2026-08-16 (user, follow-up): a click ANYWHERE on the row used
 * to toggle the item — including its text, and even the empty space below
 * the checkbox on a wrapped multi-line item. That is exactly what a click
 * elsewhere on the textbox does everywhere else (select it), so a checklist
 * item's TEXT must behave the same way; only the checkbox GLYPH toggles it.
 * Each hotspot is therefore sized to EXACTLY the checkbox's own real
 * laid-out rect (`ChecklistLinePosition.checkboxX/checkboxY/checkboxWidth/
 * checkboxHeight`, measured the same "measure, don't compute" way `y`
 * already was — mirrors `isLauncherVisible`'s precise link-icon rects, not a
 * whole-line guess) — no row-banding needed any more, so this also lifts the
 * old "a single-item checklist gets no hotspot" limitation (nothing to band
 * against a next item for; the checkbox's own rect needs no neighbour). A
 * click anywhere else on the row simply hits no hotspot and falls through to
 * the textbox's normal click/select handling, unchanged. The hit item's
 * index is read directly off the Rect via `o2dChecklistIndex`, so the click
 * handler needs no pointer-position math.
 *
 * Falls back to a geometric approximation (row top from a uniform `fontSize
 * * TEXTBOX_LINE_HEIGHT`, checkbox treated as the square `fontSize ×
 * CHECKLIST_BOX_SIZE_FACTOR` the checklist renderer itself uses) when no
 * measured `lines` are available yet — the brief pre-first-raster window —
 * same fallback the old pointer-math click handler had, now expressed as
 * hotspots too so that window is not LESS safe from the drag-stuck bug than
 * the steady state.
 */
function rebuildChecklistHotspots(
  holder: Konva.Group,
  obj: Readonly<TextBox>,
  lines: ReadonlyArray<ChecklistLinePosition>,
): void {
  holder.destroyChildren();
  // NOT `obj.listType !== 'checklist'`: that scalar is only ADVISORY (see its
  // docstring at deriveAdvisoryListType) and collapses to 'none' for any MIXED
  // document — e.g. a plain first line followed by a single checklist line,
  // a normal, supported document shape edit mode handles fine (TipTap's own
  // checkbox input doesn't consult listType at all). Gating hotspot creation
  // on it made every checklist item unclickable outside edit mode as soon as
  // the textbox held ANY non-list content too. countVisibleTaskItems walks
  // the actual document for real checklist content instead (bugreport
  // 2026-08-21, Anke: works for a single-line checklist, not for a checklist
  // line preceded by a plain line).
  if (!obj.document || countVisibleTaskItems(obj.document) === 0) return;

  const addHotspot = (
    index: number,
    left: number,
    top: number,
    hotspotWidth: number,
    height: number,
  ): void => {
    if (height <= 0 || hotspotWidth <= 0) return;
    const hotspot = new Konva.Rect({
      x: left,
      y: top,
      width: hotspotWidth,
      height,
      fill: 'black',
      opacity: 0,
      listening: true,
    });
    hotspot.setAttr('o2dChecklistIndex', index);
    holder.add(hotspot);
  };

  if (lines.length > 0) {
    lines.forEach((line, i) => {
      // Only a checkbox that is actually painted is clickable (clipped below or,
      // for an overflowing middle/bottom box, above — review 0034). The index
      // stays `i`: it is the visible-item index the toggle consumer reads.
      if (!isInsideBoxHeight(line.checkboxY, line.checkboxHeight, obj.height)) return;
      addHotspot(i, line.checkboxX, line.checkboxY, line.checkboxWidth, line.checkboxHeight);
    });
    return;
  }

  const padV = obj.paddingV ?? 0;
  const padH = obj.paddingH ?? 0;
  const lineHeight = obj.fontSize * TEXTBOX_LINE_HEIGHT;
  if (lineHeight <= 0) return;
  const checkboxSize = obj.fontSize * CHECKLIST_BOX_SIZE_FACTOR_FALLBACK;
  const count = countVisibleTaskItems(obj.document);
  for (let i = 0; i < count; i++) {
    const top = padV + i * lineHeight;
    // Same rule as the measured branch: only a box that lies inside the painted height.
    if (!isInsideBoxHeight(top, checkboxSize, obj.height)) continue;
    addHotspot(i, padH, top, checkboxSize, checkboxSize);
  }
}

/**
 * Whether the box-local point (x, y) lies inside one of the measured text line
 * areas (Anke, 2026-10-03 — hover cursor: I-beam over text, hand over empty
 * space). Lines below the visible box (`boxHeight`) do not count: content
 * past the fold is clipped and not under the pointer.
 */
export function isOverTextLine(
  lines: ReadonlyArray<TextLineRect>,
  x: number,
  y: number,
  boxHeight: number,
): boolean {
  if (y > boxHeight) return false;
  return lines.some((r) => x >= r.x && x <= r.x + r.width && y >= r.y && y <= r.y + r.height);
}

/** Internal handle that updateNode uses to re-trigger rasterization. */
export interface TextBoxGroup extends Konva.Group {
  __enqueueRaster?: (obj: Readonly<TextBox>, scale: number) => void;
  /**
   * When true, an editor overlay is currently open for this textbox. The raster
   * subscriber must NOT reveal the content bitmap while this flag is set —
   * otherwise the freshly rasterized Konva.Image ghosts behind the live
   * contentEditable overlay during in-edit style mutations (e.g. font-size
   * change via the optionsbar). Set/cleared by `TextboxEditorController`.
   */
  __editingActive?: boolean;
  /**
   * Anke, 2026-10-03 — whether a point in the group's LOCAL frame lies over
   * text (a measured text line), for the hover cursor: I-beam over text, hand
   * over empty space. Before the first raster has painted there is no
   * measurement yet; it then answers `true`, i.e. the previous behaviour.
   */
  __isOverText?: (localX: number, localY: number) => boolean;
}

/** Build a fresh Konva.Group rendering the textbox via SVG-foreignObject. */
export function createNode(
  obj: Readonly<TextBox>,
  ctx: SliceRenderContext,
): Konva.Group {
  const group: TextBoxGroup = new Konva.Group({
    id: obj.id,
    x: obj.x,
    y: obj.y,
    width: obj.width,
    height: obj.height,
    rotation: obj.rotation,
    name: 'text-box',
  });

  const hasBorder = obj.borderWidth > 0 && !!obj.borderColor;
  const bg = new Konva.Rect({
    name: TEXTBOX_BG_NAME,
    x: 0,
    y: 0,
    width: obj.width,
    height: obj.height,
    fill: obj.background || undefined,
    opacity: obj.backgroundOpacity ?? 1,
    stroke: hasBorder ? obj.borderColor : undefined,
    strokeWidth: hasBorder ? obj.borderWidth : 0,
    dash: borderDashPattern(obj),
    cornerRadius: obj.cornerRadius ?? 0,
    ...bgShadowAttrs(obj),
  });

  const content = new Konva.Image({
    name: TEXTBOX_CONTENT_NAME,
    x: 0,
    y: 0,
    width: obj.width,
    height: obj.height,
    listening: false,
    image: EMPTY_BITMAP,
    visible: false,
  });

  // PRD-RTE-OVERFLOW §TR3 — overset "+" indicator shown bottom-right when the
  // rendered content is clipped. Starts hidden; the raster subscriber toggles
  // its visibility from the measured `hasOverflow` flag.
  const overflowIndicator = createOverflowIndicator(obj, ctx.viewport.scale || 1);

  // Anke, 2026-07-19 — holder for the link click targets, refilled by the raster
  // subscriber from the measured launcher rectangles. Added ABOVE the content
  // bitmap so a press lands on the hotspot and can be stopped before the group's
  // drag begins (see `rebuildLinkHotspots`), and BELOW the overset indicator so
  // the two never fight over a click in the bottom-right corner.
  const linkHotspots = new Konva.Group({ name: LINK_HOTSPOTS_NAME });

  // Bugreport 2026-08-16 (user) — same "real child hotspot" fix as
  // `linkHotspots` (see `rebuildChecklistHotspots`), for the checklist item
  // click-to-toggle. Added BEFORE `linkHotspots` in z-order, so a link inside
  // a checklist item's text still takes precedence over the row toggle.
  const checklistHotspots = new Konva.Group({ name: CHECKLIST_HOTSPOTS_NAME });

  group.add(bg);
  group.add(content);
  group.add(checklistHotspots);
  group.add(linkHotspots);
  group.add(overflowIndicator);

  // The press must never reach the draggable group — that is what left the box
  // hanging on the cursor after returning from the opened tab.
  linkHotspots.on('mousedown touchstart', (e: Konva.KonvaEventObject<MouseEvent>) => {
    e.cancelBubble = true;
    e.evt?.stopPropagation();
  });

  linkHotspots.on('click tap', (e: Konva.KonvaEventObject<MouseEvent>) => {
    e.cancelBubble = true;
    e.evt?.stopPropagation();
    const href = e.target.getAttr('o2dHref') as string | undefined;
    if (href) openLinkUrl(href);
  });

  checklistHotspots.on('mousedown touchstart', (e: Konva.KonvaEventObject<MouseEvent>) => {
    e.cancelBubble = true;
    e.evt?.stopPropagation();
  });

  checklistHotspots.on('click tap', (e: Konva.KonvaEventObject<MouseEvent>) => {
    e.cancelBubble = true;
    e.evt?.stopPropagation();
    const idx = e.target.getAttr('o2dChecklistIndex') as number | undefined;
    if (idx === undefined) return;
    ctx.bus?.uiActions$.next({
      type: 'textbox/toggle-checklist-item',
      id: resolveLiveObject().id as ObjectId,
      lineIndex: idx,
    });
  });

  // Geometric-fallback hotspots, synchronously, so even a click BEFORE the
  // first raster completes (`cachedChecklistLines` still `[]` at that point)
  // lands on a real child hotspot instead of the bare draggable group.
  // Superseded by the real-measured-positions rebuild below the moment the
  // first raster paints.
  rebuildChecklistHotspots(checklistHotspots, obj, []);

  // Hand cursor over a launcher, so it reads as clickable in the raster where
  // there is no other affordance.
  linkHotspots.on('mouseenter', (e: Konva.KonvaEventObject<MouseEvent>) => {
    const container = e.target.getStage()?.container();
    if (container) container.style.cursor = 'pointer';
  });
  linkHotspots.on('mouseleave', (e: Konva.KonvaEventObject<MouseEvent>) => {
    const container = e.target.getStage()?.container();
    if (container) container.style.cursor = '';
  });

  // PRD-RTE-OVERFLOW §TR4 — latest world-unit required height, tracked in a
  // CLOSURE variable (NOT a Konva attr: attrs serialize into `toJSON()` and
  // would leak overflow view-state into exports, violating G2/TR9). Initialised
  // to `obj.height` so any pre-raster edge-case click of the indicator is a
  // harmless no-op (height set to its current value) rather than collapsing the
  // box to height 0. The raster subscriber refreshes it from each measurement;
  // the indicator click handler below READS it to dispatch
  // `textbox/fit-to-content` (PRD-RTE-OVERFLOW §TR5).
  let cachedRequiredHeight = obj.height;

  // Anke, 2026-07-19 — link launchers measured from the last PAINTED raster, so
  // a click hit-tests against exactly the pixels on screen. Refreshed by the
  // raster subscriber; the click handler below reads it.
  let cachedLinkTargets: ReadonlyArray<LinkTarget> = [];

  // Bugreport 2026-08-15 — same idea as cachedLinkTargets, for checklist items:
  // real per-item Y positions from the last PAINTED raster, so a click
  // hit-tests the item that's actually on screen even when items mix font
  // sizes (a line's height follows its LARGEST run — see
  // EDIT_TEXTBOX_LINE_HEIGHT — so a uniform-height assumption drifts as soon
  // as items differ in size). Feeds `rebuildChecklistHotspots` below. Empty
  // until the first raster completes, or for a non-checklist textbox — a
  // stray click in that brief pre-raster window now simply has no checklist
  // hotspot to hit (never worse than the drag-stuck bug this replaced).
  let cachedChecklistLines: ReadonlyArray<ChecklistLinePosition> = [];

  // Anke, 2026-10-03 — text line areas from the last PAINTED raster, for the
  // hover cursor (`__isOverText`). `null` until the first raster paints.
  let cachedTextLines: ReadonlyArray<TextLineRect> | null = null;
  group.__isOverText = (x, y) =>
    cachedTextLines === null ||
    isOverTextLine(cachedTextLines, x, y, resolveLiveObject().height);

  // RxJS subject + switchMap — newer raster requests cancel older in-flight
  // loads (PRD-RTE §TR2). Without switchMap, a late-arriving stale onload
  // could overwrite a freshly rasterized bitmap.
  const raster$ = new Subject<RasterRequest>();
  const rasterSub = raster$
    .pipe(switchMap((req) => rasterize$(req)))
    .subscribe((result) => {
      // Guard: do not reveal the content bitmap (nor the overflow indicator)
      // while the editor overlay is open. Otherwise an in-edit style mutation
      // (e.g. font-size change) would fire `enqueueRaster`, the freshly
      // rasterized Konva.Image would ghost behind the live contentEditable
      // overlay (PRD-RTE-PARITY-FIX G2), and the indicator would clutter the
      // edit view (PRD-RTE-OVERFLOW §TR3).
      if (!group.__editingActive) {
        content.image(result.img);
        content.visible(true);
        // O2D.W-PRD-029 §TR1 — stamp the scale marker at PAINT time, from the
        // scale this raster was actually built at. A switchMap-cancelled load
        // never reaches this subscriber, so the attr can only ever reflect a
        // bitmap that was truly drawn (fixes the "stays stale until moved"
        // rapid-zoom defect where enqueue-time stamping latched a scale whose
        // raster was discarded before decoding).
        group.setAttr(LAST_COMPUTED_SCALE_ATTR, result.scale);
        // TR3/TR4 — drive the indicator and refresh the cached auto-expand
        // height from the same measurement; both stay inside the edit guard.
        cachedRequiredHeight = result.requiredHeight;
        cachedLinkTargets = result.linkTargets;
        cachedChecklistLines = result.checklistLines;
        cachedTextLines = result.textLines;
        // Rebuild from the measurement that produced the bitmap just painted,
        // so the click targets can never describe a stale layout.
        rebuildLinkHotspots(linkHotspots, cachedLinkTargets, resolveLiveObject().height);
        rebuildChecklistHotspots(checklistHotspots, resolveLiveObject(), cachedChecklistLines);
        overflowIndicator.visible(result.hasOverflow);
      }
      group.getLayer()?.batchDraw();
    });

  /**
   * Enqueue a raster request and stamp the render-hash marker.
   *
   * O2D.W-PRD-029 §TR1/DD3 — the hash IS stamped here (enqueue time) so a
   * pending style change is not re-enqueued every tick, but the SCALE marker is
   * NOT: `LAST_COMPUTED_SCALE_ATTR` is stamped by the paint subscriber only,
   * after a raster has actually been drawn, so a switchMap-cancelled load can
   * never latch a scale it never painted.
   */
  function enqueueRaster(curObj: Readonly<TextBox>, scale: number): void {
    const dpr = getDpr();
    raster$.next({ obj: curObj, scale, dpr });
    group.setAttr(LAST_RENDER_HASH_ATTR, computeRenderHash(curObj));
  }
  group.__enqueueRaster = enqueueRaster;

  /**
   * Resolve the live `TextBox` record for this node from the object registry,
   * falling back to the creation-time `obj` snapshot when no registry lookup is
   * wired. Shared verbatim by the per-tick and trailing raster paths
   * (O2D.W-PRD-029 §TR1/§TR2) and the checklist / overflow click handlers, so
   * the "always re-read the freshest object" rule lives in exactly one place.
   */
  const resolveLiveObject = (): Readonly<TextBox> =>
    (ctx.getObject?.(obj.id as ObjectId) as Readonly<TextBox> | undefined) ?? obj;

  // Viewport subscription — re-raster on zoom changes above the threshold.
  let viewportSub: Subscription | undefined;
  if (ctx.bus?.viewport$) {
    viewportSub = ctx.bus.viewport$.subscribe((vp) => {
      // Anke, 2026-07-18 — the overset marker is screen-constant, so it must be
      // re-laid-out on EVERY zoom tick. Deliberately ahead of both the editing
      // guard and the 5% re-raster gate below: those exist to skip expensive
      // rasterization, but skipping the marker layout would leave it visibly
      // mis-sized until some unrelated change happened to open the gate.
      //
      // Box size is read off the background rect, NOT via `resolveLiveObject()`:
      // this runs on every single zoom tick, and a board-state lookup per tick is
      // exactly what the destroy-leak test guards against. `bg` is kept at the
      // object's size by `updateNode`, so it is an equivalent source.
      layoutOverflowIndicator(overflowIndicator, bg.width(), bg.height(), vp.scale || 1);

      // O2D.W-PRD-029 §TR1 — while the editor overlay is open the bitmap is
      // hidden, so a zoom tick must NOT enqueue: the raster would only force a
      // synchronous `buildTextboxSvg`/`measureOverflow` reflow on a bitmap the
      // user cannot see, and the paint subscriber (guarded by the same flag)
      // would suppress it anyway. Editing resumes the per-tick path on close.
      if (group.__editingActive) {
        return;
      }
      const lastScale = group.getAttr(LAST_COMPUTED_SCALE_ATTR) as
        | number
        | undefined;
      const newScale = vp.scale || 1;
      if (
        lastScale === undefined ||
        lastScale === 0 ||
        Math.abs(newScale - lastScale) / lastScale >= RE_RASTERIZE_THRESHOLD
      ) {
        enqueueRaster(resolveLiveObject(), newScale);
      }
    });
  }

  // O2D.W-PRD-029 §TR2 — trailing raster on zoom-settle. A rapid wheel burst can
  // end on a sub-5% settle tick that the per-tick gate above skips, leaving the
  // bitmap painted at a slightly stale scale (soft final frame). A debounced pipe
  // off `viewport$` fires ONCE the burst settles (~150ms of quiet) and re-enqueues
  // at the settled scale iff the painted scale differs from it — EXACT inequality,
  // not the 5% threshold. The exact check catches the sub-threshold settle frame
  // (painted scale ≠ current by <5%) while a genuine no-op tick (already painted at
  // exactly this scale) enqueues nothing, so the path never self-retriggers; the
  // debounce bounds it to at most one enqueue per settle. Gated behind
  // `!__editingActive` (mirror of the per-tick TR1 gate): during editing the bitmap
  // is hidden, so a trailing raster would only force a wasted synchronous reflow.
  // `vp.scale` is the SINGLE source for BOTH the compare and the enqueue, so the
  // compared scale and the enqueued scale can never disagree at settle time.
  let trailingSub: Subscription | undefined;
  if (ctx.bus?.viewport$) {
    trailingSub = ctx.bus.viewport$
      .pipe(debounceTime(TRAILING_RASTER_DEBOUNCE_MS))
      .subscribe((vp) => {
        if (group.__editingActive) {
          return;
        }
        const settledScale = vp.scale || 1;
        const paintedScale = group.getAttr(LAST_COMPUTED_SCALE_ATTR) as
          | number
          | undefined;
        if (paintedScale === undefined || paintedScale !== settledScale) {
          enqueueRaster(resolveLiveObject(), settledScale);
        }
      });
  }

  // Initial rasterization at the current viewport scale.
  enqueueRaster(obj, ctx.viewport.scale || 1);

  // Cleanup body — Konva does not emit a synchronous `destroy` event in
  // this version, so we wrap `destroy()` directly. Element-wiring code may
  // also call `node.destroy()` on object removal so both removal paths
  // converge here (PRD-RTE §TR2 cleanup strategy: prevents subscription
  // leaks across object add/remove cycles).
  const originalDestroy = group.destroy.bind(group);
  group.destroy = function (): Konva.Group {
    raster$.complete();
    rasterSub.unsubscribe();
    viewportSub?.unsubscribe();
    // O2D.W-PRD-029 §TR2 — tear down the trailing-raster pipe too. Unsubscribing
    // the debounced subscription also clears any PENDING trailing timer, so a
    // burst that settles after the node is destroyed cannot fire on a dead node.
    trailingSub?.unsubscribe();
    return originalDestroy();
  } as typeof group.destroy;

  // Stop the press from reaching the draggable parent Group. Konva begins a
  // node drag on `mousedown`/`touchstart` (the textbox Group is made draggable
  // by `wireObjectNode`), so cancelling the bubble only in the `click` handler
  // is too late — the drag has already started and the box sticks to the
  // cursor after the click. Cancel it here, before the drag begins.
  overflowIndicator.on('mousedown touchstart', (e: Konva.KonvaEventObject<MouseEvent>) => {
    e.cancelBubble = true;
    e.evt?.stopPropagation();
  });

  // PRD-RTE-OVERFLOW §TR5 — clicking the overset "+" indicator requests an
  // auto-expand. We dispatch a `textbox/fit-to-content` UiAction carrying the
  // latest measured `cachedRequiredHeight`; the lifecycle controller (T4.3)
  // translates it into an undoable `UpdatePropertyCommand(height)`. The renderer
  // itself never mutates `BoardState` — it only signals intent (Command-Bus).
  overflowIndicator.on('click tap', (e: Konva.KonvaEventObject<MouseEvent>) => {
    e.cancelBubble = true;
    e.evt?.stopPropagation();
    const cur = resolveLiveObject();
    ctx.bus?.uiActions$.next({
      type: 'textbox/fit-to-content',
      id: cur.id as ObjectId,
      requiredHeight: cachedRequiredHeight,
    });
  });

  // Anke, 2026-07-18 — the marker hovers like a corner handle: it grows and
  // darkens on the same curve, via the shared helper, so the whole board keeps
  // one interaction language. The "+" inverts to white as the box darkens, or it
  // would disappear into the fill. Replaces the earlier cursor-only affordance
  // (the hand cursor is still set, by the helper).
  // Same themable colour the transformer anchors use: the CSS custom property
  // when the host sets one, the shared constant otherwise. Read through the
  // stage container, which inherits it from the whiteboard element.
  const handleHoverFill = (): string => {
    const container = overflowIndicator.getStage()?.container();
    if (!container) return HANDLE_HOVER_FILL;
    return (
      getComputedStyle(container).getPropertyValue('--o2d-handle-hover-fill').trim() ||
      HANDLE_HOVER_FILL
    );
  };
  const overflowBox = overflowIndicator.findOne<Konva.Rect>(`.${OVERFLOW_BOX_NAME}`);
  const overflowGlyph = overflowIndicator.findOne<Konva.Text>(`.${OVERFLOW_GLYPH_NAME}`);
  if (overflowBox && overflowGlyph) {
    wireHoverFillGroup(
      overflowIndicator,
      [
        { shape: overflowBox, hover: handleHoverFill, resting: OVERFLOW_INDICATOR.FILL },
        {
          shape: overflowGlyph,
          hover: () => OVERFLOW_INDICATOR.GLYPH_HOVER_FILL,
          resting: OVERFLOW_INDICATOR.STROKE,
        },
      ],
      'pointer',
    );
  }

  return group;
}

/** Apply changes from an updated TextBox onto its existing Konva.Group. */
export function updateNode(
  node: Konva.Node,
  obj: Readonly<TextBox>,
  ctx: SliceRenderContext,
): void {
  if (!(node instanceof Konva.Group)) return;
  // Base geometry (group x/y/width/height/rotation) is applied centrally by
  // `applyBaseGeometry`; this method only refreshes the inner background and
  // the rasterised text image from the new size.
  const bg = node.findOne<Konva.Rect>(`.${TEXTBOX_BG_NAME}`);
  if (bg) {
    const hasBorder = obj.borderWidth > 0 && !!obj.borderColor;
    bg.width(obj.width);
    bg.height(obj.height);
    bg.fill(obj.background || (undefined as unknown as string));
    bg.opacity(obj.backgroundOpacity ?? 1);
    bg.stroke(hasBorder ? obj.borderColor : (undefined as unknown as string));
    bg.strokeWidth(hasBorder ? obj.borderWidth : 0);
    bg.dash(borderDashPattern(obj));
    bg.cornerRadius(obj.cornerRadius ?? 0);
    bg.setAttrs(bgShadowAttrs(obj));
  }

  const content = node.findOne<Konva.Image>(`.${TEXTBOX_CONTENT_NAME}`);
  if (content) {
    content.x(0);
    content.y(0);
    content.width(obj.width);
    content.height(obj.height);
  }

  // PRD-RTE-OVERFLOW §TR3 — keep the overset indicator pinned to the bottom-right
  // corner when the box is resized. Visibility is owned by the raster subscriber.
  const overflowIndicator = node.findOne<Konva.Group>(
    `.${OVERFLOW_INDICATOR_NAME}`,
  );
  if (overflowIndicator) {
    layoutOverflowIndicator(overflowIndicator, obj.width, obj.height, ctx.viewport.scale || 1);
  }

  // Re-raster decision: any render-hash change OR a scale-delta above the
  // 5% gate triggers a fresh rasterization (PRD-RTE §TR4).
  const enqueue = (node as TextBoxGroup).__enqueueRaster;
  if (enqueue) {
    const lastScale = node.getAttr(LAST_COMPUTED_SCALE_ATTR) as
      | number
      | undefined;
    const lastHash = node.getAttr(LAST_RENDER_HASH_ATTR) as string | undefined;
    const newScale = ctx.viewport.scale || 1;
    const newHash = computeRenderHash(obj);
    const hashChanged = lastHash !== newHash;
    const scaleGateOpen =
      lastScale === undefined ||
      lastScale === 0 ||
      Math.abs(newScale - lastScale) / lastScale >= RE_RASTERIZE_THRESHOLD;
    if (hashChanged || scaleGateOpen) {
      enqueue(obj, newScale);
    }
  }

  node.getLayer()?.batchDraw();
}
